import React, { useState, useEffect } from 'react';
import { Bell, Check, Trash2, Settings, RefreshCw } from 'lucide-react';
import api from '../services/api';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const endpoint = filter === 'unread' ? '/notifications/unread' : '/notifications';
      const response = await api.get(endpoint);
      setNotifications(response || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, isRead: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const filteredNotifications = notifications;

  return (
    <div className="notifications-page">
      <div className="page-header">
        <h1><Bell size={24} /> Notifications</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchNotifications}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <a href="/notification-settings" className="btn btn-secondary">
            <Settings size={18} />
            Settings
          </a>
        </div>
      </div>

      <div className="notifications-toolbar">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread
          </button>
        </div>

        {notifications.some(n => !n.isRead) && (
          <button className="btn btn-sm btn-primary" onClick={markAllAsRead}>
            <Check size={16} />
            Mark all as read
          </button>
        )}
      </div>

      <div className="notifications-container">
        {loading ? (
          <div className="notifications-loading">Loading notifications...</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="notifications-empty">
            <Bell size={64} />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-card ${!notification.isRead ? 'unread' : ''}`}
              >
                <div className="notification-indicator">
                  {!notification.isRead && <div className="unread-dot"></div>}
                </div>
                
                <div className="notification-body">
                  <div className="notification-header-row">
                    <h3 className="notification-title">{notification.title}</h3>
                    <span className="notification-time">{formatTime(notification.createdAt)}</span>
                  </div>
                  
                  <p className="notification-message">{notification.message}</p>
                  
                  <div className="notification-actions-row">
                    {!notification.isRead && (
                      <button
                        className="notification-btn"
                        onClick={() => markAsRead(notification.id)}
                      >
                        <Check size={16} />
                        Mark as read
                      </button>
                    )}
                    <button
                      className="notification-btn delete"
                      onClick={() => deleteNotification(notification.id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
